import { useState } from 'react';
import { searchPapers } from '../api';

const CURRENT_YEAR = new Date().getFullYear();

export default function SearchBar({ onResults }) {
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState('relevance');
  const [yearFrom, setYearFrom] = useState('');
  const [yearTo, setYearTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSearch = async () => {
    const trimmed = query.trim();
    if (!trimmed) return;
    if (yearFrom && yearTo && parseInt(yearFrom) > parseInt(yearTo)) {
      setError('"From" year cannot be greater than "To" year.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await searchPapers(
        trimmed,
        sort,
        yearFrom ? parseInt(yearFrom) : null,
        yearTo ? parseInt(yearTo) : null,
      );
      onResults(response.data, trimmed);
    } catch (err) {
      setError(err.response?.data?.detail || 'Search failed. Please check that the backend is running.');
      onResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  const activeFilterCount = [sort !== 'relevance', !!yearFrom, !!yearTo].filter(Boolean).length;

  return (
    <div className="w-full">
      <div className="flex gap-2">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="e.g. CRISPR knockout protocol, Western blot, RNA-seq, PCR..."
          className="flex-1 rounded-md px-3 py-2 text-sm text-[#171717] placeholder-[#808080] bg-white focus:outline-none focus:ring-2 focus:ring-[#0a72ef]"
          style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
          disabled={loading}
        />

        <button
          onClick={() => setShowFilters((v) => !v)}
          className={`px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5 ${
            showFilters || activeFilterCount > 0
              ? 'bg-[#171717] text-white'
              : 'bg-white text-[#4d4d4d] hover:text-[#171717]'
          }`}
          style={showFilters || activeFilterCount > 0 ? {} : { boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
          title="Filters"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L13 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 017 21v-7.586L3.293 6.707A1 1 0 013 6V4z" />
          </svg>
          {activeFilterCount > 0 && (
            <span className="bg-white text-[#171717] text-xs rounded-full w-4 h-4 flex items-center justify-center font-bold">
              {activeFilterCount}
            </span>
          )}
        </button>

        <button
          onClick={handleSearch}
          disabled={loading || !query.trim()}
          className="px-4 py-2 bg-[#171717] text-white rounded-md font-medium text-sm
                     hover:bg-[#2e2e2e] disabled:opacity-50 disabled:cursor-not-allowed
                     transition-colors flex items-center gap-2"
        >
          {loading ? (
            <>
              <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Searching...
            </>
          ) : 'Search'}
        </button>
      </div>

      {showFilters && (
        <div
          className="mt-2 p-3 bg-white rounded-lg flex flex-wrap gap-4 items-end"
          style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
        >
          <div>
            <label className="block text-xs font-medium text-[#808080] mb-1">Sort by</label>
            <div className="flex rounded-md overflow-hidden" style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}>
              {[
                { value: 'relevance', label: 'Relevance' },
                { value: 'newest', label: 'Newest' },
                { value: 'oldest', label: 'Oldest' },
              ].map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setSort(opt.value)}
                  className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                    sort === opt.value
                      ? 'bg-[#171717] text-white'
                      : 'bg-white text-[#4d4d4d] hover:bg-[#fafafa]'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-[#808080] mb-1">Publication year</label>
            <div className="flex items-center gap-1.5">
              <input
                type="number"
                value={yearFrom}
                onChange={(e) => setYearFrom(e.target.value)}
                placeholder="From"
                min="1900"
                max={CURRENT_YEAR}
                className="w-20 rounded px-2 py-1.5 text-xs text-[#171717] placeholder-[#808080] bg-white focus:outline-none focus:ring-2 focus:ring-[#0a72ef]"
                style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
              />
              <span className="text-[#808080] text-xs">–</span>
              <input
                type="number"
                value={yearTo}
                onChange={(e) => setYearTo(e.target.value)}
                placeholder="To"
                min="1900"
                max={CURRENT_YEAR}
                className="w-20 rounded px-2 py-1.5 text-xs text-[#171717] placeholder-[#808080] bg-white focus:outline-none focus:ring-2 focus:ring-[#0a72ef]"
                style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-[#808080] mb-1">Quick range</label>
            <div className="flex gap-1">
              {[
                { label: 'Last year', from: CURRENT_YEAR - 1 },
                { label: '5 years', from: CURRENT_YEAR - 5 },
                { label: '10 years', from: CURRENT_YEAR - 10 },
              ].map((p) => (
                <button
                  key={p.label}
                  onClick={() => { setYearFrom(String(p.from)); setYearTo(String(CURRENT_YEAR)); }}
                  className={`px-2 py-1 text-xs rounded transition-colors ${
                    yearFrom === String(p.from) && yearTo === String(CURRENT_YEAR)
                      ? 'bg-[#171717] text-white'
                      : 'bg-white text-[#4d4d4d] hover:text-[#171717]'
                  }`}
                  style={yearFrom === String(p.from) && yearTo === String(CURRENT_YEAR)
                    ? {}
                    : { boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {activeFilterCount > 0 && (
            <button
              onClick={() => { setSort('relevance'); setYearFrom(''); setYearTo(''); }}
              className="text-xs text-red-500 hover:text-red-700 font-medium self-end pb-0.5"
            >
              Reset filters
            </button>
          )}
        </div>
      )}

      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
    </div>
  );
}
